import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Chart } from 'angular-highcharts';
import { add, compareAsc } from 'date-fns';
import { PointOptionsObject, SeriesOptionsType } from 'highcharts';
import { FFDriverShiftPredictionBlocksEntity, FFShifts } from '../../ff-shift.service';

@Component({
  selector: 'lohi-shift-tool-graph',
  templateUrl: './shift-tool-graph.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShiftToolGraphComponent implements OnInit, OnChanges {
  @Input() public data: FFShifts;
  @Output() public selectedGroup = new EventEmitter<FFDriverShiftPredictionBlocksEntity>();

  public chart: Chart;

  constructor() {}

  public ngOnInit(): void {
    this.setupChart();
  }

  public setupChart() {
    const data = this.data?.ffShiftCalendar.FFDriverShiftPredictionBlocks.slice().sort((a, b) =>
      compareAsc(
        add(new Date(a.Date), { hours: a.StartHourOfDay }),
        add(new Date(b.Date), { hours: b.StartHourOfDay }),
      ),
    );

    this.chart = new Chart({
      chart: {
        type: 'column',
      },
      title: null,
      credits: {
        enabled: false,
      },
      time: {
        useUTC: false,
        timezoneOffset: new Date().getTimezoneOffset(),
      },
      yAxis: {
        labels: {
          enabled: true,
        },
        title: { text: 'Drivers' },
      },
      plotOptions: {
        series: {
          stacking: 'normal',
          label: {
            enabled: true,
          },
          events: {
            click: (event) => {
              this.selectedGroup.emit(event.point.options.custom.data);
            },
          },
        },
      },
      tooltip: {
        useHTML: true,
        pointFormatter() {
          return `<span style="color: ${this.color}">${
            this.options.custom.name
          }</span> <span class="font-semibold">${this.y.toLocaleString()}</span><br>${this.options.custom.description}`;
        },
      },
      xAxis: {
        type: 'datetime',
        visible: true,
      },
      series: [],
    });

    const good: Array<[number | string, number | null] | PointOptionsObject> = [];
    const bad: Array<[number | string, number | null] | PointOptionsObject> = [];
    const neutral: Array<[number | string, number | null] | PointOptionsObject> = [];
    for (const datum of data) {
      for (const driverGroup of datum.DriverGroups) {
        if (!driverGroup.Drivers || driverGroup.Drivers.length === 0) {
          continue;
        }
        if (driverGroup.Status === 'good') {
          good.push({
            x: add(new Date(datum.Date), { hours: datum.StartHourOfDay }).getTime(),
            y: driverGroup.Drivers.length,
            custom: {
              data: datum,
              description: driverGroup.Description,
              name: driverGroup.Name,
            },
          });
        } else if (driverGroup.Status === 'bad') {
          bad.push({
            x: add(new Date(datum.Date), { hours: datum.StartHourOfDay }).getTime(),
            y: driverGroup.Drivers.length,
            custom: {
              data: datum,
              description: driverGroup.Description,
              name: driverGroup.Name,
            },
          });
        } else {
          neutral.push({
            x: add(new Date(datum.Date), { hours: datum.StartHourOfDay }).getTime(),
            y: driverGroup.Drivers.length,
            custom: {
              data: datum,
              description: driverGroup.Description,
              name: driverGroup.Name,
            },
          });
        }
      }
    }

    if (bad.length > 0) {
      this.chart.addSeries(
        {
          data: bad,
          type: 'column',
          fillOpacity: 1,
          color: '#EF6048',
          name: (bad[0] as PointOptionsObject)?.custom?.name,
        } as SeriesOptionsType,
        true,
        true,
      );
    }

    if (good.length > 0) {
      this.chart.addSeries(
        {
          data: good,
          type: 'column',
          fillOpacity: 1,
          color: '#37AE51',
          name: (good[0] as PointOptionsObject)?.custom?.name,
        } as SeriesOptionsType,
        true,
        true,
      );
    }

    if (neutral.length > 0) {
      this.chart.addSeries(
        {
          data: neutral,
          type: 'column',
          fillOpacity: 1,
          color: '#92A0AD',
          name: (neutral[0] as PointOptionsObject)?.custom?.name,
        } as SeriesOptionsType,
        true,
        true,
      );
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.setupChart();
  }
}
